import type { Database } from "~~/types/database"


interface KvEntry {
  key: string
  value: string
  created_at: string
  modified_at: string | null
}

export const useKvStore = defineStore('kv', () => {
  // State
  const entries = ref<Map<string, KvEntry>>(new Map())
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const isInitialized = ref(false)
  const loadPromise = ref<Promise<any> | null>(null)
  let realtimeSubscription: ReturnType<ReturnType<typeof useSupabaseClient>['channel']> | null = null

  // Get a single value by key - with wait for loading
  const getValue = async (key: string): Promise<string | null> => {
    // If we're loading, wait for it to complete
    if (isLoading.value && loadPromise.value) {
      await loadPromise.value
    }
    
    // If we haven't initialized yet, do it now
    if (!isInitialized.value) {
      await initialize()
    }
    
    return entries.value.get(key)?.value || null
  }
  
  // Synchronous version - doesn't wait for loading
  const getValueSync = (key: string): string | null => {
    return entries.value.get(key)?.value || null
  }

  // Get all key-value pairs
  const getAll = computed(() => {
    const result: Record<string, string> = {}
    entries.value.forEach((entry, key) => {
      result[key] = entry.value
    })
    return result
  })

  // Load all KV entries from the database
  const loadEntries = async () => {
    if (isLoading.value && loadPromise.value) {
      return loadPromise.value
    }
    
    isLoading.value = true
    error.value = null
    
    // Create a new promise for this load operation
    loadPromise.value = new Promise(async (resolve, reject) => {
      try {
        const supabase = useSupabaseClient<Database>()
        const { data, error: fetchError } = await supabase
          .from('kv_storage')
          .select('key, value, created_at, modified_at')
        
        if (fetchError) throw fetchError
        
        // Update entries map
        entries.value.clear()
        data.forEach(entry => {
          entries.value.set(entry.key, entry as KvEntry)
        })
        
        resolve(data)
      } catch (err) {
        error.value = err as Error
        console.error('Error loading KV entries:', err)
        reject(err)
      } finally {
        isLoading.value = false
      }
    })
    
    return loadPromise.value
  }

  // Setup realtime subscription
  const setupRealtimeSubscription = () => {
    // Clean up existing subscription if any
    cleanupRealtimeSubscription()
    
    const supabase = useSupabaseClient<Database>()
    
    console.log('[KvStore] Setting up realtime subscription')
    
    realtimeSubscription = supabase.channel('kv-changes')
      .on(
        'postgres_changes',
        {
          event: '*', 
          schema: 'public',
          table: 'kv_storage'
        },
        async (payload) => {
          console.log('[KvStore] Received realtime update:', payload)
          
          // Handle different event types
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const newEntry = payload.new as KvEntry
            entries.value.set(newEntry.key, newEntry)
          } else if (payload.eventType === 'DELETE') {
            const oldEntry = payload.old as KvEntry
            entries.value.delete(oldEntry.key)
          }
        }
      )
      .subscribe()
  }

  // Clean up realtime subscription
  const cleanupRealtimeSubscription = () => {
    if (realtimeSubscription) {
      console.log('[KvStore] Cleaning up realtime subscription')
      realtimeSubscription.unsubscribe()
      realtimeSubscription = null
    }
  }

  // Initialize store
  const initialize = async () => {
    if (isInitialized.value) return
    
    console.log('[KvStore] Initializing')
    
    try {
      await loadEntries()
      setupRealtimeSubscription()
      isInitialized.value = true
    } catch (err) {
      console.error('[KvStore] Initialization error:', err)
    }
  }

  // Clean up on unmount
  onUnmounted(() => {
    cleanupRealtimeSubscription()
  })

  return {
    // State
    entries,
    isLoading,
    error,
    isInitialized,
    
    // Getters
    getValue,
    getValueSync, // Non-async version
    getAll,
    
    // Actions
    loadEntries,
    initialize,
    setupRealtimeSubscription,
    cleanupRealtimeSubscription
  }
})